import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';
import { useMemo } from 'react';

// Query Keys for Admin Privacy Policy
export const adminPrivacyPolicyKeys = {
  all: ['adminPrivacyPolicy'],
  lists: () => [...adminPrivacyPolicyKeys.all, 'list'],
  list: (filters) => [...adminPrivacyPolicyKeys.lists(), { filters }],
  details: () => [...adminPrivacyPolicyKeys.all, 'detail'],
  detail: (id) => [...adminPrivacyPolicyKeys.details(), id],
  settings: () => [...adminPrivacyPolicyKeys.all, 'settings'],
};

/**
 * Custom hook to fetch a list of privacy policy sections for admin.
 * @param {object} params - Query parameters for filtering and pagination.
 * @param {object} options - React Query options.
 */
export const useAdminPrivacyPolicy = (params = {}, options = {}) => {
  const queryClient = useQueryClient();

  const cleanFilters = useMemo(() => {
    const newFilters = { ...params };
    // Remove undefined or null values
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key] === undefined || newFilters[key] === null || newFilters[key] === '') {
        delete newFilters[key];
      }
    });
    return newFilters;
  }, [params]);

  const cleanFiltersWithPagination = useMemo(() => {
    return {
      ...cleanFilters,
      per_page: cleanFilters.per_page || 10,
    };
  }, [cleanFilters]);

  const { data: sectionsResponse, isLoading, error } = useQuery({
    queryKey: adminPrivacyPolicyKeys.list(cleanFiltersWithPagination),
    queryFn: async () => {
      const response = await api.get('/admin/privacy-policy', { params: cleanFiltersWithPagination });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options,
  });

  const sections = useMemo(() => sectionsResponse?.data?.data || [], [sectionsResponse]);
  const pagination = useMemo(() => sectionsResponse?.data?.meta || {}, [sectionsResponse]);

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/privacy-policy', data);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminPrivacyPolicyKeys.lists());
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/privacy-policy/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminPrivacyPolicyKeys.lists());
      queryClient.invalidateQueries(adminPrivacyPolicyKeys.detail(variables.id));
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/privacy-policy/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminPrivacyPolicyKeys.lists());
      queryClient.invalidateQueries(adminPrivacyPolicyKeys.detail(variables));
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/privacy-policy/${id}`);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminPrivacyPolicyKeys.lists());
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const { data: settings, isLoading: isLoadingSettings } = useQuery({
    queryKey: adminPrivacyPolicyKeys.settings(),
    queryFn: async () => {
      const response = await api.get('/admin/privacy-policy/settings');
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put('/admin/privacy-policy/settings', data);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(adminPrivacyPolicyKeys.settings());
      variables.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      variables.onError?.(error);
    }
  });

  const activeStatusOptions = useMemo(() => [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
  ], []);

  return {
    sections,
    isLoading,
    error,
    filters: cleanFilters,
    pagination,
    activeStatusOptions,
    statistics: {},
    isLoadingStatistics: false,
    statisticsError: null,
    createSection: createMutation.mutate,
    isCreating: createMutation.isLoading,
    updateSection: updateMutation.mutate,
    isUpdating: updateMutation.isLoading,
    toggleStatus: toggleStatusMutation.mutate,
    isToggling: toggleStatusMutation.isLoading,
    deleteSection: deleteMutation.mutate,
    isDeleting: deleteMutation.isLoading,
    settings: settings || {},
    isLoadingSettings,
    updateSettings: updateSettingsMutation.mutate,
    isUpdatingSettings: updateSettingsMutation.isLoading,
  };
};

